import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Col, Divider, Row } from 'antd';
import { maxBy } from 'es-toolkit';

import { formateAmount } from '@/utilz';
import EmptyRecords from '@/views/components/Common/EmptyRecords';
import ScrollPagination from '@/views/components/Paginations/ScrollPagination';
import Paper from '@/views/components/Paper/Paper';
import { useGetOCClaimSettlements, useOCClaimScrollPagination } from '@/views/screens/Claims/OverCharge/hooks';
import FooterContentCard from '@/views/screens/common/FooterContentCard';

import Button from '../../../../../../../components/Buttons/Button';
import PrimaryLabel from '../../../../../../../components/Labels/PrimaryLabel';
import OverChargeSettlementCard from './OverChargeSettlementCard';

const OverChargeSettlements = forwardRef(({ claimDetails }, ref) => {
  const fbOcKey = claimDetails?.fbOcKey;
  const {
    data: claimSettlementsQueryData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    isSuccess,
  } = useGetOCClaimSettlements({ fbOcKey });

  const claimSettlements = useMemo(
    () => ({
      data: claimSettlementsQueryData?.flatSettlements ?? [],
      totalAmount: claimSettlementsQueryData?.totalAmount ?? 0,
      totalRows: claimSettlementsQueryData?.totalRows ?? 0,
    }),
    [claimSettlementsQueryData],
  );

  const [settlementList, setSettlementList] = useState([]);
  const [dirtySettlementKeys, setDirtySettlementKeys] = useState(new Set());
  const [deletedSettlements, setDeletedSettlements] = useState([]);

  const filteredSettlements = useMemo(() => {
    if (!claimDetails?.fbOcKey || !Array.isArray(claimSettlements.data)) {
      return [];
    }

    return claimSettlements.data.filter((settlement) => Number(settlement.fbocKey) === Number(claimDetails.fbOcKey));
  }, [claimDetails?.fbOcKey, claimSettlements.data]);

  const settlementFormRefs = useRef(new Map());
  const settlementListRef = useRef(settlementList);
  const dirtySettlementKeysRef = useRef(dirtySettlementKeys);
  const filteredSettlementsRef = useRef(filteredSettlements);
  const lastClaimKeyRef = useRef(null);
  const deletedSettlementsRef = useRef(deletedSettlements);

  useEffect(() => {
    settlementListRef.current = settlementList;
  }, [settlementList]);

  useEffect(() => {
    dirtySettlementKeysRef.current = dirtySettlementKeys;
  }, [dirtySettlementKeys]);

  useEffect(() => {
    filteredSettlementsRef.current = filteredSettlements;
  }, [filteredSettlements]);

  useEffect(() => {
    deletedSettlementsRef.current = deletedSettlements;
  }, [deletedSettlements]);

  useEffect(() => {
    const currentFbOcKey = claimDetails?.fbOcKey;
    if (!currentFbOcKey) {
      return;
    }
    if (lastClaimKeyRef.current !== null && lastClaimKeyRef.current !== currentFbOcKey) {
      setSettlementList([]);
      setDeletedSettlements([]);
      setDirtySettlementKeys(new Set());
      settlementFormRefs.current.clear();
    }
    lastClaimKeyRef.current = currentFbOcKey;
  }, [claimDetails?.fbOcKey]);

  const getSettlementKey = (settlement) => settlement.settlementSeq;

  const markSettlementDirty = (settlementKey) => {
    setDirtySettlementKeys((prev) => new Set(prev).add(settlementKey));
  };

  const registerSettlementForm = (settlementKey, formInstance) => {
    if (formInstance) {
      settlementFormRefs.current.set(settlementKey, formInstance);
    } else {
      settlementFormRefs.current.delete(settlementKey);
    }
  };

  useImperativeHandle(ref, () => ({
    getSettlementsData: () => {
      const originalSettlements = filteredSettlementsRef.current || [];
      const originalSettlementKeys = new Set(originalSettlements.map((s) => s.settlementSeq));

      const settlementsToProcess =
        settlementListRef.current.length > 0
          ? settlementListRef.current
          : originalSettlements.map((settlement) => ({
              ...settlement,
              amount: Number(settlement.amount),
              editMode: true,
              isNew: false,
            }));

      const activeSettlements = settlementsToProcess.map((settlement) => {
        const settlementKey = getSettlementKey(settlement);
        const form = settlementFormRefs.current.get(settlementKey);
        const formData = form?.getFieldsValue() ?? settlement;

        let dirtyFlag = 'N';

        const existsInOriginal = originalSettlementKeys.has(settlementKey);
        const isDirty = dirtySettlementKeysRef.current.has(settlementKey);

        if (settlement.isNew) {
          dirtyFlag = existsInOriginal ? (isDirty ? 'U' : 'N') : 'I';
        } else if (existsInOriginal) {
          dirtyFlag = isDirty ? 'U' : 'N';
        } else if (isDirty) {
          dirtyFlag = 'U';
        }

        return { ...formData, dirtyFlag };
      });

      const deletedSettlementsData = deletedSettlementsRef.current.map((settlement) => ({
        ...settlement,
        dirtyFlag: 'D',
      }));

      return [...activeSettlements, ...deletedSettlementsData];
    },

    validateAllSettlements: async () => {
      try {
        const forms = [...settlementFormRefs.current.values()].filter(Boolean);
        if (forms.length === 0) {
          return Promise.resolve();
        }
        await Promise.all(
          forms.map((form) => {
            if (!form || typeof form.validateFields !== 'function') {
              return Promise.resolve();
            }
            return form.validateFields();
          }),
        );
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }));

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync list when query data updates
  useEffect(() => {
    if (!isSuccess || claimSettlements.data === undefined) {
      return;
    }
    const deletedSettlementKeys = new Set(
      deletedSettlementsRef.current.map((settlement) => getSettlementKey(settlement)).filter((key) => key),
    );
    setSettlementList(
      claimSettlements.data
        .filter((data) => {
          const settlementKey = getSettlementKey(data);
          return !deletedSettlementKeys.has(settlementKey);
        })
        .map((data) => ({
          ...data,
          amount: Number(data.amount),
          editMode: true,
          isNew: false,
        })),
    );
    setDirtySettlementKeys(new Set());
    setDeletedSettlements([]);
  }, [claimSettlements.data, isSuccess]);

  const { handleNextPage, scrollHasMore } = useOCClaimScrollPagination({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    loadedCount: claimSettlements.data?.length ?? 0,
    totalRows: claimSettlements.totalRows ?? 0,
  });

  const isSettlementsLoading = isLoading || (isFetching && !isFetchingNextPage);

  const handleSubmit = (_data, index) => {
    const settlement = settlementList[index];
    if (settlement && !settlement.isNew) {
      markSettlementDirty(getSettlementKey(settlement));
    }
  };

  const handleDelete = (index) => {
    const settlementToDelete = settlementList[index];
    if (!settlementToDelete) {
      return;
    }

    if (settlementToDelete.isNew) {
      const remainingSettlements = settlementList.filter((_, i) => i !== index);
      setSettlementList(remainingSettlements);
      const settlementKey = getSettlementKey(settlementToDelete);
      settlementFormRefs.current.delete(settlementKey);
    } else {
      const remainingSettlements = settlementList.filter((_, i) => i !== index);
      setSettlementList(remainingSettlements);
      const settlementKey = getSettlementKey(settlementToDelete);
      settlementFormRefs.current.delete(settlementKey);
      setDeletedSettlements((prev) => [...prev, settlementToDelete]);
    }
  };

  const handleAddNew = () => {
    const allSettlements = [...filteredSettlements, ...settlementList];
    const maxSettlement = maxBy(allSettlements, ({ settlementSeq }) => Number(settlementSeq));
    const nextSeq = (maxSettlement?.settlementSeq || 0) + 1;

    setSettlementList([
      {
        accountingCode: undefined,
        amount: undefined,
        dirtyFlag: 'i',
        editMode: true,
        fbOcKey: claimDetails.fbOcKey,
        fbocKey: 0,
        isNew: true,
        settlementSeq: nextSeq,
      },
      ...settlementList,
    ]);
  };

  return (
    <div className="d-flex  py-16 h-100" style={{ flexDirection: 'column' }}>
      <Row className="px-30 align-items-center" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">Overcharge Claims Settlements</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16}>
            <Col>
              <Button onClick={handleAddNew}>Add New Settlement</Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100 " />
      {settlementList.length > 0 || isSettlementsLoading ? (
        <ScrollPagination
          className="px-30 py-10 flex-1"
          loadMore={handleNextPage}
          isLoading={isFetchingNextPage}
          hasMore={scrollHasMore}
          scrollOffset={30}
          disabledForceNextPage
          isSkipScrollToBottom
        >
          {settlementList.map((row, index) => (
            <OverChargeSettlementCard
              settlementDetails={row}
              key={row.settlementSeq}
              index={index}
              handleSubmit={handleSubmit}
              handleDelete={handleDelete}
              onFieldChange={() => markSettlementDirty(getSettlementKey(row))}
              registerForm={registerSettlementForm}
            />
          ))}
        </ScrollPagination>
      ) : (
        <EmptyRecords />
      )}
      <div className="px-30 mt-20">
        <Paper className="background-header px-16 py-5">
          <Row justify="space-around" className="px-10" gutter={10}>
            <Col sm={8} />
            <Col sm={8}>
              <FooterContentCard
                align="middle"
                justify="start"
                count={formateAmount(filteredSettlements.length > 0 ? claimSettlements.totalAmount : 0)}
                label="Total Amount"
                contentClass="text-white f-16 fw-600"
                labelClass="text-light f-14"
              />
            </Col>
            <Col sm={8} />
          </Row>
        </Paper>
      </div>
    </div>
  );
});

export default OverChargeSettlements;
